'use client';

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { PortalLink, useCart, useCommerceSource, useBlockPart, tx } from '@akademi/site-builder-runtime';
import { BlockRoot, useAsyncResource } from '@akademi/site-builder-blocks';

type Localized = string | Record<string, string>;

/**
 * Puts a product in the basket.
 *
 * <p>Takes a slug rather than a product id, because a slug is what a page knows
 * and what an operator can type into the editor; the id is resolved through the
 * source. On a product page the slug is left unset and read from the URL, the
 * same way {@code ProductDetail} does it, so one placement works for every
 * product.
 *
 * <p>Renders nothing at all when the host mounts no cart. That is not a
 * failure state to explain to a shopper — a quote-led site simply does not sell
 * this way, and an "unavailable" button would be worse than no button. The
 * editor placeholder is shown instead when the block is being authored.
 */
export interface AddToCartButtonProps {
  /** Explicit slug. Unset means "the product this page is about". */
  productSlug?: string;
  label?: Localized;
  /** Shown while this product is already in the basket. */
  addedLabel?: Localized;
  quantity?: number;
  cartHref?: string;
  /** Send the shopper to the basket after adding. */
  goToCartHref?: string;
  fullWidth?: boolean;
}

function slugFromPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const segments = window.location.pathname.split('/').filter(Boolean);
  return segments.length > 0 ? decodeURIComponent(segments[segments.length - 1]!) : undefined;
}

export function AddToCartButton({
  productSlug,
  label,
  addedLabel,
  quantity = 1,
  cartHref = '/cart',
  goToCartHref,
  fullWidth = false,
}: AddToCartButtonProps) {
  const cart = useCart();
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');

  const slug = (productSlug ?? slugFromPath() ?? '').trim();

  // The button needs the product's id, which only the detail read carries.
  const product = useAsyncResource(async () => {
    if (!commerce || !slug) return null;
    return commerce.getProductBySlug(slug);
  }, [commerce, slug]);

  if (!cart || !cart.available) return null;

  // A discriminated union, so the product is only reachable through the
  // 'loaded' arm — which is also the only state in which the button can work.
  const loaded = product.kind === 'loaded' ? product.data : null;
  const productId = loaded?.id;
  const outOfStock = loaded?.availability === 'OUT_OF_STOCK';
  const disabled = cart.busy || productId == null || outOfStock;
  const cartQuantity = cart.cart?.lines
    .filter((line) => line.productId === productId && line.parentLineId == null)
    .reduce((total, line) => total + Number(line.quantity), 0) ?? 0;

  async function onAdd() {
    if (productId == null || !cart) return;
    const succeeded = await cart.add({ productId, quantity: String(quantity) });
    if (succeeded && goToCartHref) window.location.assign(goToCartHref);
  }

  return (
    <BlockRoot as="div">
      {cartQuantity > 0 ? (
        <PortalLink
          {...part('button')}
          href={cartHref}
          style={{ ...buttonStyle, ...inCartStyle, ...(fullWidth ? { width: '100%' } : null) }}
        >
          {tx(addedLabel)
            ? `${tx(addedLabel)} (${cartQuantity})`
            : t('commerce.cart.inCart', { count: cartQuantity })}
        </PortalLink>
      ) : (
        <button
          {...part('button')}
          type="button"
          onClick={() => void onAdd()}
          disabled={disabled}
          style={{ ...buttonStyle, ...(fullWidth ? { width: '100%' } : null), ...(disabled ? disabledStyle : null) }}
        >
          {outOfStock ? t('commerce.availability.outOfStock') : tx(label) || t('commerce.cart.add')}
        </button>
      )}
      {cart.error && (
        <p {...part('error')} role="alert" style={errorStyle}>
          {cart.error}
        </p>
      )}
    </BlockRoot>
  );
}

const buttonStyle: CSSProperties = {
  padding: '12px 24px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-primary, #2563eb)',
  color: 'var(--color-on-primary, #fff)',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
};
const disabledStyle: CSSProperties = { opacity: 0.55, cursor: 'not-allowed' };
const inCartStyle: CSSProperties = { textDecoration: 'none', boxSizing: 'border-box' };
const errorStyle: CSSProperties = {
  margin: '8px 0 0',
  color: 'var(--color-danger-700, #b91c1c)',
  fontSize: 14,
};
